import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface SpiritualHealthStepProps {
  data?: any;
  profileData?: any;
  onComplete: (data: any) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface SurveyData {
  // Section 1: Spiritual Health Awareness
  currentWalkWithLord: string;
  intimacyWithChristHindrances: string;
  spiritualDrynessResponse: string;
  soulGuarding: string;
  restAndSabbathRhythms: string;
  recentConvictionResponse: string;
  
  // Section 2: Emotional Health Awareness
  emotionalStabilityDescription: string;
  pressureCriticismResponse: string;
  stressDisappointmentHandling: string;
  healthyBoundaries: string;
  currentEmotionalState: string;
  burnoutPrevention: string;
  
  // Section 3: Calling and Resilience
  godsCallDescription: string;
  callConfirmationByOthers: string;
  difficultMoments: string;
  discerningGodsLeading: string;
  faithAndWisdomBalance: string;
  churchPlantingFears: string;
  
  // Section 4: Relational Health Awareness
  feedbackCorrectionResponse: string;
  conflictResolutionApproach: string;
  shepherdingDifficultPeople: string;
  fosteringUnity: string;
  relationalIntegrity: string;
  accountabilityRelationships: string;
  
  // Section 5: Spiritual Warfare Health Awareness
  spiritualAttackRecognition: string;
  prayerInDecisions: string;
  guardingAgainstCynicism: string;
  godlyProvisionExample: string;
  prayingTogether: string;
  sustainingScriptures: string;
  
  // Section 6: Accountability Health Awareness
  pastorMentorAccountability: string;
  discipleshipFrequency: string;
  teachability: string;
  isolationPrevention: string;
  spouseSupportNeeds: string;
  handlingUnappreciation: string;
}

const initialSurveyData: SurveyData = {
  currentWalkWithLord: '',
  intimacyWithChristHindrances: '',
  spiritualDrynessResponse: '',
  soulGuarding: '',
  restAndSabbathRhythms: '',
  recentConvictionResponse: '',
  emotionalStabilityDescription: '',
  pressureCriticismResponse: '',
  stressDisappointmentHandling: '',
  healthyBoundaries: '',
  currentEmotionalState: '',
  burnoutPrevention: '',
  godsCallDescription: '',
  callConfirmationByOthers: '',
  difficultMoments: '',
  discerningGodsLeading: '',
  faithAndWisdomBalance: '',
  churchPlantingFears: '',
  feedbackCorrectionResponse: '',
  conflictResolutionApproach: '',
  shepherdingDifficultPeople: '',
  fosteringUnity: '',
  relationalIntegrity: '',
  accountabilityRelationships: '',
  spiritualAttackRecognition: '',
  prayerInDecisions: '',
  guardingAgainstCynicism: '',
  godlyProvisionExample: '',
  prayingTogether: '',
  sustainingScriptures: '',
  pastorMentorAccountability: '',
  discipleshipFrequency: '',
  teachability: '',
  isolationPrevention: '',
  spouseSupportNeeds: '',
  handlingUnappreciation: '',
};

const SpiritualHealthStep: React.FC<SpiritualHealthStepProps> = ({ 
  data, 
  profileData,
  onComplete, 
  onNext, 
  onPrevious 
}) => {
  const [candidateSurvey, setCandidateSurvey] = useState<SurveyData>(initialSurveyData);
  const [spouseSurvey, setSpouseSurvey] = useState<SurveyData>(initialSurveyData);
  const [currentSurveyType, setCurrentSurveyType] = useState<'candidate' | 'spouse'>('candidate');
  const [currentSection, setCurrentSection] = useState(0);
  const [candidateSection, setCandidateSection] = useState(0);
  const [spouseSection, setSpouseSection] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [candidateCompleted, setCandidateCompleted] = useState(false);
  const [spouseCompleted, setSpouseCompleted] = useState(false);

  const isMarried = profileData?.maritalStatus?.toLowerCase() === 'married';

  const sections = [
    {
      title: 'Section 1: Spiritual Health Awareness',
      fields: [
        { key: 'currentWalkWithLord', label: 'How would you describe your current walk with the Lord?' },
        { key: 'intimacyWithChristHindrances', label: 'What tends to pull you away from intimacy with Christ?' },
        { key: 'spiritualDrynessResponse', label: 'How do you handle seasons of spiritual dryness or discouragement?' },
        { key: 'soulGuarding', label: 'How do you guard your soul from pride, burnout, or comparison in ministry?' },
        { key: 'restAndSabbathRhythms', label: 'How do you intentionally cultivate rest and Sabbath rhythms?' },
        { key: 'recentConvictionResponse', label: 'Describe a recent time when you sensed the Lord convicting you of sin. How did you respond?' }
      ]
    },
    {
      title: 'Section 2: Emotional Health Awareness',
      fields: [
        { key: 'emotionalStabilityDescription', label: 'How would those closest to you describe your emotional stability and temperament?' },
        { key: 'pressureCriticismResponse', label: 'How do you respond under pressure, criticism, or conflict?' },
        { key: 'stressDisappointmentHandling', label: 'How do you typically deal with stress or disappointment?' },
        { key: 'healthyBoundaries', label: 'How do you maintain healthy boundaries in ministry and relationships?' },
        { key: 'currentEmotionalState', label: 'How would you describe your current emotional state — energized, weary, content, anxious, hopeful, etc.?' },
        { key: 'burnoutPrevention', label: 'When you recognize signs of emotional fatigue or burnout, what steps do you take?' }
      ]
    },
    {
      title: 'Section 3: Calling and Resilience',
      fields: [
        { key: 'godsCallDescription', label: 'Describe how you sensed God\'s call to church planting.' },
        { key: 'callConfirmationByOthers', label: 'How has that call been confirmed through others?' },
        { key: 'difficultMoments', label: 'What have been the most difficult moments in pursuing that call?' },
        { key: 'discerningGodsLeading', label: 'How do you discern God\'s leading when plans change or doors close?' },
        { key: 'faithAndWisdomBalance', label: 'How do you balance faith and wisdom when taking risks in ministry?' },
        { key: 'churchPlantingFears', label: 'What fears do you have about church planting?' }
      ]
    },
    {
      title: 'Section 4: Relational Health Awareness',
      fields: [
        { key: 'feedbackCorrectionResponse', label: 'How do you typically respond to feedback or correction from others?' },
        { key: 'conflictResolutionApproach', label: 'Describe your approach to resolving conflict biblically.' },
        { key: 'shepherdingDifficultPeople', label: 'How do you shepherd difficult or divisive people?' },
        { key: 'fosteringUnity', label: 'How do you foster unity among leaders or volunteers?' },
        { key: 'relationalIntegrity', label: 'How do you ensure relational integrity and accountability in ministry?' },
        { key: 'accountabilityRelationships', label: 'Who currently knows you well enough to speak truthfully into your life?' }
      ]
    },
    {
      title: 'Section 5: Spiritual Warfare Health Awareness',
      fields: [
        { key: 'spiritualAttackRecognition', label: 'How do you recognize and respond to spiritual attack in your life or ministry?' },
        { key: 'prayerInDecisions', label: 'What role does prayer play in your daily decisions and leadership?' },
        { key: 'guardingAgainstCynicism', label: 'How do you guard against cynicism or unbelief in hard seasons?' },
        { key: 'godlyProvisionExample', label: 'Describe a time God provided or intervened when you felt helpless.' },
        { key: 'prayingTogether', label: 'How do you and your spouse pray together for your family and the mission?' },
        { key: 'sustainingScriptures', label: 'What passages of Scripture most sustain you in spiritual battle?' }
      ]
    },
    {
      title: 'Section 6: Accountability Health Awareness',
      fields: [
        { key: 'pastorMentorAccountability', label: 'Who serves as your pastor, mentor, or primary accountability partner?' },
        { key: 'discipleshipFrequency', label: 'How often are you in intentional discipleship or mentoring relationships?' },
        { key: 'teachability', label: 'How teachable are you — and how do you invite correction?' },
        { key: 'isolationPrevention', label: 'What practices keep you from isolation in ministry?' },
        { key: 'spouseSupportNeeds', label: 'What kind of support would your spouse need in this season?' },
        { key: 'handlingUnappreciation', label: 'How do you handle seasons when you feel unnoticed or unappreciated?' }
      ]
    }
  ];

  useEffect(() => {
    loadExistingData();
  }, []);

  // Listen for navigation events from sidebar and update parent
  useEffect(() => {
    const handleNavigateToSection = (event: CustomEvent) => {
      const sectionIndex = event.detail;
      if (sectionIndex >= 0 && sectionIndex < sections.length) {
        setCurrentSection(sectionIndex);
        // Update section tracking for current survey type
        if (currentSurveyType === 'candidate') {
          setCandidateSection(sectionIndex);
        } else {
          setSpouseSection(sectionIndex);
        }
        // Notify parent component to update sidebar highlight
        const updateEvent = new CustomEvent('subsectionChanged', { detail: sectionIndex });
        window.dispatchEvent(updateEvent);
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener('navigateToSection' as any, handleNavigateToSection as EventListener);
    return () => {
      window.removeEventListener('navigateToSection' as any, handleNavigateToSection as EventListener);
    };
  }, [currentSurveyType, sections.length]);

  // Check if a section is complete for the current survey (all fields have content)
  const isSectionComplete = (sectionIndex: number, surveyData: SurveyData): boolean => {
    const section = sections[sectionIndex];
    if (!section) return false;
    
    return section.fields.every(field => {
      const value = (surveyData as any)[field.key];
      return value && value.trim().length > 0;
    });
  };

  // Calculate completion status for all sections of both surveys
  const getSectionCompletionStatus = () => {
    const candidateCompletions = sections.map((_, index) => isSectionComplete(index, candidateSurvey));
    const spouseCompletions = isMarried 
      ? sections.map((_, index) => isSectionComplete(index, spouseSurvey))
      : [];
    
    return {
      candidate: candidateCompletions,
      spouse: spouseCompletions
    };
  };

  // Notify parent when section changes internally (via next/prev buttons or tab switch)
  useEffect(() => {
    const updateEvent = new CustomEvent('subsectionChanged', { detail: currentSection });
    window.dispatchEvent(updateEvent);
  }, [currentSection]);

  // Notify parent of completion status whenever survey data or survey type changes
  useEffect(() => {
    const completionStatus = getSectionCompletionStatus();
    const updateEvent = new CustomEvent('subsectionCompletionChanged', { 
      detail: { 
        candidate: completionStatus.candidate,
        spouse: completionStatus.spouse,
        isMarried: isMarried
      }
    });
    window.dispatchEvent(updateEvent);
  }, [candidateSurvey, spouseSurvey, isMarried]);

  const loadExistingData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/portal-candidates/onboarding/spiritual-health', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.candidateSurvey) {
        setCandidateSurvey({ ...initialSurveyData, ...response.data.candidateSurvey });
        if (response.data.candidateSurvey.status === 'submitted') {
          setCandidateCompleted(true);
        }
      }

      if (response.data.spouseSurvey) {
        setSpouseSurvey({ ...initialSurveyData, ...response.data.spouseSurvey });
        if (response.data.spouseSurvey.status === 'submitted') {
          setSpouseCompleted(true);
        }
      }

      // Don't auto-switch - let users choose which survey to view/edit via tabs
    } catch (error) {
      console.error('Error loading spiritual health surveys:', error);
    }
  };

  const getCurrentSurvey = () => {
    return currentSurveyType === 'candidate' ? candidateSurvey : spouseSurvey;
  };

  const setCurrentSurvey = (data: SurveyData) => {
    if (currentSurveyType === 'candidate') {
      setCandidateSurvey(data);
    } else {
      setSpouseSurvey(data);
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    const currentSurvey = getCurrentSurvey();
    const updatedSurvey = { ...currentSurvey, [field]: value };
    setCurrentSurvey(updatedSurvey);
    
    // Auto-save draft after a short delay
    autoSaveDraft(updatedSurvey);
  };

  // Debounced auto-save
  const autoSaveTimer = React.useRef<NodeJS.Timeout | null>(null);
  const autoSaveDraft = (surveyData: SurveyData) => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }

    autoSaveTimer.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post('/api/portal-candidates/onboarding/spiritual-health/draft', {
          ...surveyData,
          surveyFor: currentSurveyType
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSaveStatus('Draft saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } catch (error) {
        console.error('Error auto-saving draft:', error);
      }
    }, 1000);
  };

  const handleSubmitSurvey = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const currentSurvey = getCurrentSurvey();
      
      const response = await axios.post('/api/portal-candidates/onboarding/spiritual-health', {
        ...currentSurvey,
        surveyFor: currentSurveyType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const nowCandidateCompleted = currentSurveyType === 'candidate' ? true : candidateCompleted;
      const nowSpouseCompleted = currentSurveyType === 'spouse' ? true : spouseCompleted;

      if (currentSurveyType === 'candidate') {
        setCandidateCompleted(true);
      } else {
        setSpouseCompleted(true);
      }

      // Check if all required surveys are now complete and notify parent
      const allComplete = isMarried
        ? (nowCandidateCompleted && nowSpouseCompleted)
        : nowCandidateCompleted;

      if (allComplete) {
        onComplete({
          candidateCompleted: nowCandidateCompleted,
          spouseCompleted: nowSpouseCompleted,
          isMarried,
        });
      }

      // Show success message but allow continued editing
      setSaveStatus('Survey submitted successfully');
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        setSaveStatus('');
      }, 4000);
    } catch (error: any) {
      console.error('Error submitting survey:', error);
      alert(error.response?.data?.error || 'Failed to submit survey');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSectionNext = async () => {
    // Save current progress to database before moving to next section
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    
    try {
      const token = localStorage.getItem('token');
      const currentSurvey = getCurrentSurvey();
      
      await axios.post('/api/portal-candidates/onboarding/spiritual-health/draft', {
        ...currentSurvey,
        surveyFor: currentSurveyType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSaveSuccess(true);
      
      // Brief delay to show success message
      setTimeout(() => {
        if (currentSection < sections.length - 1) {
          const newSection = currentSection + 1;
          setCurrentSection(newSection);
          // Update section tracking for current survey type
          if (currentSurveyType === 'candidate') {
            setCandidateSection(newSection);
          } else {
            setSpouseSection(newSection);
          }
          setSaveSuccess(false);
          window.scrollTo(0, 0);
        }
      }, 800);
    } catch (error) {
      console.error('Error saving spiritual health section:', error);
      setSaveError('Failed to save your progress. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSectionPrevious = () => {
    if (currentSection > 0) {
      const newSection = currentSection - 1;
      setCurrentSection(newSection);
      // Update section tracking for current survey type
      if (currentSurveyType === 'candidate') {
        setCandidateSection(newSection);
      } else {
        setSpouseSection(newSection);
      }
      window.scrollTo(0, 0);
    }
  };

  const isCurrentSurveyComplete = () => {
    return currentSurveyType === 'candidate' ? candidateCompleted : spouseCompleted;
  };

  const handleSurveyTypeChange = async (newType: 'candidate' | 'spouse') => {
    if (newType === currentSurveyType) return;

    // Save current survey before switching
    if (currentSection > 0) {
      try {
        const token = localStorage.getItem('token');
        const currentSurvey = getCurrentSurvey();
        await axios.post('/api/portal-candidates/onboarding/spiritual-health/draft', {
          ...currentSurvey,
          surveyFor: currentSurveyType
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error saving before switch:', error);
      }
    }

    // Switch to the other survey type
    setCurrentSurveyType(newType);
    // Restore the section position for the survey type being switched to
    if (newType === 'candidate') {
      setCurrentSection(candidateSection);
    } else {
      setCurrentSection(spouseSection);
    }
    window.scrollTo(0, 0);
  };

  const currentSurveyData = getCurrentSurvey();
  const currentSectionData = sections[currentSection];
  const progress = ((currentSection + 1) / sections.length) * 100;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Spiritual/Emotional Health Assessment
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {isMarried 
            ? 'Complete the assessment for both yourself and your spouse. You can switch between them at any time to review or edit your answers.' 
            : 'Complete this assessment to help us understand your spiritual and emotional health.'}
        </p>
        
        {/* Tab Navigation for Candidate/Spouse */}
        {isMarried && (
          <div className="mt-4 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => handleSurveyTypeChange('candidate')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${currentSurveyType === 'candidate'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                Candidate Assessment
                {candidateCompleted && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Completed
                  </span>
                )}
              </button>
              <button
                onClick={() => handleSurveyTypeChange('spouse')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${currentSurveyType === 'spouse'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                Spouse Assessment
                {spouseCompleted && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Completed
                  </span>
                )}
              </button>
            </nav>
          </div>
        )}
        <div className="mt-4 p-4 bg-green-50 rounded-md border border-green-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <strong>Auto-save enabled:</strong> Your progress will be saved automatically after each section. You can safely come back and continue later.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            Section {currentSection + 1} of {sections.length}
          </span>
          {saveStatus && (
            <span className="text-sm text-green-600">{saveStatus}</span>
          )}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Current section */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">
          {currentSectionData.title}
        </h3>

        <div className="space-y-6">
          {currentSectionData.fields.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {field.label}
              </label>
              <textarea
                rows={4}
                value={(currentSurveyData as any)[field.key] || ''}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Type your response here..."
              />
            </div>
          ))}
        </div>
      </div>

      {/* Save Success Message */}
      {saveSuccess && (
        <div className="mb-6 p-4 bg-green-50 rounded-md border border-green-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-700">
                {saveStatus && saveStatus.includes('submitted')
                  ? `${currentSurveyType === 'candidate' ? 'Candidate' : 'Spouse'} survey submitted successfully! You can still edit it if needed.`
                  : saveStatus || 'Progress saved successfully!'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Save Error Message */}
      {saveError && (
        <div className="mb-6 p-4 bg-red-50 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{saveError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <button
          type="button"
          onClick={currentSection === 0 && currentSurveyType === 'candidate' && (!isMarried || candidateSection === 0) ? onPrevious : handleSectionPrevious}
          disabled={isLoading || isSaving}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {currentSection === 0 && currentSurveyType === 'candidate' && (!isMarried || candidateSection === 0) ? 'Previous Step' : 'Previous Section'}
        </button>

        <div className="flex gap-2">
          {currentSection < sections.length - 1 ? (
            <button
              type="button"
              onClick={handleSectionNext}
              disabled={isLoading || isSaving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  Save & Continue
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmitSurvey}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading 
                ? 'Submitting...' 
                : isCurrentSurveyComplete() 
                  ? `Re-submit ${currentSurveyType === 'candidate' ? 'Candidate' : 'Spouse'} Survey`
                  : `Submit ${currentSurveyType === 'candidate' ? 'Candidate' : 'Spouse'} Survey`}
            </button>
          )}
        </div>
      </div>

      {/* Show button to skip to next step if both surveys are complete */}
      {candidateCompleted && (!isMarried || spouseCompleted) && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={onNext}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            All Surveys Complete - Continue to Next Step →
          </button>
        </div>
      )}
    </div>
  );
};

export default SpiritualHealthStep;

